import type { LucideIcon } from 'lucide-react'
import {
  Building2, Users, LayoutDashboard, GraduationCap, ClipboardList, BookOpen,
  Video, Radio, Megaphone, CalendarDays, Trophy, MessageCircleQuestion,
  Boxes, LineChart, LifeBuoy, SlidersHorizontal, School, Swords, Database, ShieldAlert, Receipt, Send, Smartphone,
  Activity, Settings2,
  KeyRound, // google-apple-1021
  Lock, // free/paid control (21 Oct 2026)
  Shirt, // --- Novi Wardrobe (21 Oct 2026, branch wardrobe-items-1022) ---
  ToggleRight, // Rollout switches (app 1.0.126, 21 Oct 2026)
  Home, // admin revamp, 31 Oct 2026
  BellRing, // automatic notifications per school (31 Oct 2026)
  UserPlus, // registrations per day (1 Nov 2026)
} from 'lucide-react'
import type { Role } from '@/types/auth'

export type { Role }
export type StaffRole = Exclude<Role, 'STUDENT'> // STUDENT is blocked from the admin app

/** Minimal shape from GET /auth/me that the nav logic needs. */
export interface MenuUser {
  role: Role
  is_default_school: boolean
}

// Landing route after login. Admin revamp, 31 Oct 2026: every role starts on Home.
export const LANDING_ROUTE: Record<Role, string> = {
  ADMIN: '/home',
  SCHOOL_ADMIN: '/home',
  PRINCIPAL: '/home',
  TEACHER: '/home',
  STUDENT: '/login', // never reaches admin app; safety fallback
}

/**
 * Menu sections (admin revamp, 31 Oct 2026 — Omar: "build it all in one go"; plan
 * admin_plan_fragment.html §2). HOME is the unlabelled first group. Each section folds;
 * the sidebar remembers what was folded.
 */
export type MenuSection =
  | 'HOME' | 'PEOPLE' | 'MY_GRADES' | 'TEACHING' | 'CONTENT' | 'REPORTS' | 'MESSAGES' | 'MONEY'
  | 'AI_COST' | 'APP_SETTINGS' | 'SETTINGS'

export const SECTION_LABEL: Record<MenuSection, string> = {
  HOME: '',
  PEOPLE: 'People',
  MY_GRADES: 'My grades',
  TEACHING: 'Teaching',
  CONTENT: 'Content',
  REPORTS: 'Reports',
  MESSAGES: 'Messages',
  MONEY: 'Money',
  AI_COST: 'AI & Cost',
  APP_SETTINGS: 'App Settings',
  SETTINGS: 'Settings',
}

interface MenuItem {
  key: string
  label: string
  route: string
  icon: LucideIcon
  /** A hub: one menu entry over several existing screens, shown as tabs (see HUBS). */
  hub?: HubKey
}

/** Registry of every nav destination (shared across roles). */
const ITEMS = {
  home:        { key: 'home',        label: 'Home',            route: '/home',                     icon: Home },
  // People
  students:    { key: 'students',    label: 'Students',        route: '/students',                 icon: School, hub: 'students' },
  mystudents:  { key: 'mystudents',  label: 'My Students',     route: '/students',                 icon: School, hub: 'students' },
  staff:       { key: 'staff',       label: 'Staff',           route: '/users',                    icon: Users },
  schools:     { key: 'schools',     label: 'Schools',         route: '/schools',                  icon: Building2 },
  // Teaching
  tests:       { key: 'tests',       label: 'Exams',           route: '/tests',                    icon: ClipboardList, hub: 'exams' },
  bank:        { key: 'bank',        label: 'Question Bank',   route: '/question-bank',            icon: Boxes },
  questions:   { key: 'questions',   label: 'Teacher Questions', route: '/teacher-questions',      icon: MessageCircleQuestion },
  timetable:   { key: 'timetable',   label: 'Timetable',       route: '/timetable',                icon: CalendarDays },
  // Content
  books:       { key: 'books',       label: 'Books',           route: '/books',                    icon: BookOpen },
  videos:      { key: 'videos',      label: 'Videos',          route: '/videos',                   icon: Video },
  live:        { key: 'live',        label: 'Live Sessions',   route: '/live-sessions',            icon: Radio },
  curriculum:  { key: 'curriculum',  label: 'Curriculum',      route: '/grades',                   icon: GraduationCap, hub: 'curriculum' },
  // Reports
  dashboard:   { key: 'dashboard',   label: 'Performance',     route: '/dashboard',                icon: LayoutDashboard },
  // One screen since 9 Oct 2026 (Omar): it replaced Message Usage (/usage) and Student Activity
  // (/student-activity), whose addresses now redirect to it.
  // Streaks became its tab (Omar, 4 Nov 2026: "streak ... should in the reports part, engagement").
  activity:    { key: 'activity',    label: 'Engagement',      route: '/activity',                 icon: Activity, hub: 'engagement' },
  challenges:  { key: 'challenges',  label: 'Challenges',      route: '/challenge-activity',       icon: Swords },
  leaderboard: { key: 'leaderboard', label: 'Leaderboard',     route: '/leaderboard',              icon: Trophy },
  // 1 Nov 2026 (Omar): new registrations and subscriptions per day; a school sees its own only (the API scopes it).
  signups:     { key: 'signups',     label: 'Registrations',   route: '/registrations',            icon: UserPlus },
  // Messages
  announce:    { key: 'announce',    label: 'Announcements',   route: '/announcements',            icon: Megaphone },
  support:     { key: 'support',     label: 'Support',         route: '/support',                  icon: LifeBuoy, hub: 'support' },
  // Money
  receipts:    { key: 'receipts',    label: 'Receipts',        route: '/receipts',                 icon: Receipt },
  subproblems: { key: 'subproblems', label: 'Subscription Problems', route: '/subscription-problems', icon: ShieldAlert },
  // Free/paid per country (21 Oct 2026) + the free plan and the streak (30 Oct 2026), one hub.
  // No free-month tab: Omar, 31 Oct 2026, "No separate free-month screen (no new free-month
  // signups after Nov 1); show the free-month end date on the student page."
  freepaid:    { key: 'freepaid',    label: 'Free & Paid',     route: '/feature-access',           icon: Lock, hub: 'freepaid' },
  // AI & cost
  cost:        { key: 'cost',        label: 'Cost',            route: '/monitoring',               icon: LineChart, hub: 'cost' },
  cache:       { key: 'cache',       label: 'Gemini Cache',    route: '/cache',                    icon: Database },
  telegram:    { key: 'telegram',    label: 'Telegram',        route: '/telegram',                 icon: Send },
  // App settings
  // Rollout (app 1.0.126, 21 Oct 2026): Off / Testers / Everyone per new feature, no build or deploy.
  rollout:     { key: 'rollout',     label: 'Rollout & Testers', route: '/rollout',                icon: ToggleRight },
  signin:      { key: 'signin',      label: 'Sign-in & Phone', route: '/sign-in-methods',          icon: KeyRound, hub: 'signin' },
  // App versions in their own place (31 Oct 2026): out of Challenges Rules and Phone Codes.
  appversions: { key: 'appversions', label: 'App Versions',    route: '/app-versions',             icon: Smartphone },
  // 12 Oct 2026 (Omar): what a school allows in challenges; every new part is switched on from here.
  challengeset: { key: 'challengeset', label: 'Challenges Rules', route: '/challenge-settings',    icon: Settings2 },
  // --- Novi Wardrobe (21 Oct 2026, branch wardrobe-items-1022): every item's price and shown/hidden ---
  wardrobe:    { key: 'wardrobe',    label: 'Novi Wardrobe',   route: '/mascot-wardrobe',          icon: Shirt },
  prompts:     { key: 'prompts',     label: 'Test Prompts',    route: '/test-prompts',             icon: SlidersHorizontal },
  // 31 Oct 2026 (Omar): every automatic notification per school: switch, text, Improve with Gemini, Egypt drafts.
  autopush:    { key: 'autopush',    label: 'Automatic Notifications', route: '/auto-pushes',      icon: BellRing },
} satisfies Record<string, MenuItem>

export type ItemKey = keyof typeof ITEMS

/** Hubs: one menu entry over several existing screens, which keep their own addresses (old
 *  links still open) and show a tab bar on top (AppLayout → HubTabs). A tab is shown only to the
 *  roles ROUTE_ROLES lets in, and only when its showIf holds. */
export type HubKey = 'students' | 'exams' | 'curriculum' | 'support' | 'engagement' | 'freepaid' | 'cost' | 'signin'

export interface HubTab {
  label: string
  route: string
  showIf?: (u: MenuUser) => boolean
}

export const HUBS: Record<HubKey, HubTab[]> = {
  // Class View became the "Grade table" of the one student list (plan §2). Grades only, no classes.
  students: [
    { label: 'List', route: '/students' },
    { label: 'Grade table', route: '/class-view' },
  ],
  // Grades only, no classes (Omar, 31 Oct 2026): the Classes screen is gone; /classes → /grades.
  // Curriculum coverage (2 Nov 2026): lesson by lesson, the exams and the bank behind each lesson. A teacher has
  // only the list (the route is SCHOOL), so he sees no tab bar.
  exams: [
    { label: 'Exams', route: '/tests' },
    { label: 'Coverage', route: '/coverage' },
  ],
  curriculum: [
    { label: 'Grades', route: '/grades' },
    { label: 'Subjects', route: '/subjects' },
    { label: 'Timetable', route: '/timetable', showIf: (u) => !u.is_default_school },
  ],
  support: [
    { label: 'Tickets', route: '/support' },
    { label: 'Before sign-in', route: '/support/guests' },
    // The knowledge base Gemini reads on the support page (10 Oct 2026). The server allows the
    // admins of the schools in SUPPORT_ASSIST_SCHOOL_IDS; a default school is the nearest the menu knows.
    { label: 'Knowledge', route: '/support/knowledge', showIf: (u) => u.role === 'ADMIN' || u.is_default_school },
  ],
  // Omar, 4 Nov 2026: "in admin need to check who started chain, all activities related to chains of student",
  // then "streak is not in the correct screen or section ... it should in the reports part, engagement": the
  // Streaks report is a tab of Engagement (platform admin only, so a school's roles see no tab bar there).
  // The streak's SETTINGS stay in Free & Paid ("Free plan & streak").
  engagement: [
    { label: 'Engagement', route: '/activity' },
    { label: 'Streaks', route: '/streaks' },
  ],
  freepaid: [
    { label: 'Countries', route: '/feature-access' },
    { label: 'Free plan & streak', route: '/free-plan' },
  ],
  cost: [
    { label: 'Overview', route: '/monitoring' },
    { label: 'Breakdown', route: '/monitoring/cost-explorer' },
    // Omar, 1 Nov 2026: running vs one-time, per day / per month, by service, from the real Google bill.
    { label: 'Running vs one-time', route: '/monitoring/running' },
  ],
  signin: [
    { label: 'Sign-in methods', route: '/sign-in-methods' },
    { label: 'Phone codes', route: '/phone-codes' },
  ],
}

interface NavEntry {
  item: ItemKey
  showIf?: (u: MenuUser) => boolean // conditional visibility (default-school gates)
}
interface NavSection {
  section: MenuSection
  entries: NavEntry[]
}

const onDefault = (u: MenuUser) => u.is_default_school

/** The school menu: Omar, 31 Oct 2026, "School admin and principal: two roles, one menu, the
 *  principal is read-only." Same entries for both; the principal sees every page view-only. */
const SCHOOL_MENU: NavSection[] = [
  { section: 'HOME', entries: [{ item: 'home' }] },
  { section: 'PEOPLE', entries: [{ item: 'students' }, { item: 'staff' }] },
  // Omar, 15 Oct 2026: the school admin reads which units and lessons each exam covers (read-only list).
  { section: 'TEACHING', entries: [{ item: 'tests' }, { item: 'bank' }, { item: 'questions' }] },
  {
    section: 'CONTENT',
    entries: [{ item: 'books', showIf: onDefault }, { item: 'videos' }, { item: 'live' }, { item: 'curriculum' }],
  },
  { section: 'REPORTS', entries: [{ item: 'dashboard' }, { item: 'activity' }, { item: 'challenges' }, { item: 'leaderboard' }, { item: 'signups' }] },
  {
    section: 'MESSAGES',
    entries: [
      { item: 'announce' }, { item: 'support' },
      { item: 'autopush' }, // automatic notifications per school (31 Oct 2026)
    ],
  },
  {
    section: 'SETTINGS',
    // only default schools sell subscriptions
    entries: [{ item: 'subproblems', showIf: onDefault }, { item: 'challengeset' }],
  },
]

/** Single source of truth for the sidebar, per role, in render order. */
export const MENU_BY_ROLE: Record<StaffRole, NavSection[]> = {
  ADMIN: [
    { section: 'HOME', entries: [{ item: 'home' }] },
    { section: 'PEOPLE', entries: [{ item: 'students' }, { item: 'staff' }, { item: 'schools' }] },
    { section: 'TEACHING', entries: [{ item: 'tests' }, { item: 'bank' }, { item: 'questions' }] },
    { section: 'CONTENT', entries: [{ item: 'books' }, { item: 'videos' }, { item: 'live' }, { item: 'curriculum' }] },
    { section: 'REPORTS', entries: [{ item: 'dashboard' }, { item: 'activity' }, { item: 'challenges' }, { item: 'leaderboard' }, { item: 'signups' }] },
    {
      section: 'MESSAGES',
      entries: [
        { item: 'announce' }, { item: 'support' },
        { item: 'autopush' }, // automatic notifications per school (31 Oct 2026)
      ],
    },
    { section: 'MONEY', entries: [{ item: 'receipts' }, { item: 'subproblems' }, { item: 'freepaid' }] },
    { section: 'AI_COST', entries: [{ item: 'cost' }, { item: 'cache' }, { item: 'telegram' }] },
    {
      section: 'APP_SETTINGS',
      entries: [{ item: 'rollout' }, { item: 'signin' }, { item: 'appversions' }, { item: 'challengeset' }, { item: 'wardrobe' }, { item: 'prompts' }],
    },
  ],
  SCHOOL_ADMIN: SCHOOL_MENU,
  PRINCIPAL: SCHOOL_MENU,
  // Omar, 31 Oct 2026: "A teacher sees only the subjects he teaches. The grade teacher sees
  // everything in his grade." The server scopes every one of these pages (app/core/teacher_scope.py).
  TEACHER: [
    { section: 'HOME', entries: [{ item: 'home' }] },
    {
      section: 'MY_GRADES',
      entries: [{ item: 'mystudents' }, { item: 'dashboard' }, { item: 'leaderboard' }, { item: 'challenges' }],
    },
    {
      section: 'TEACHING',
      entries: [
        { item: 'tests' }, { item: 'bank' }, { item: 'questions' },
        { item: 'timetable', showIf: (u) => !u.is_default_school }, // non-default only
      ],
    },
  ],
}

export interface ResolvedMenuItem extends MenuItem {
  /** Every route that lights this entry up (its own + its hub's tabs). */
  routes: string[]
}
export interface ResolvedMenuSection {
  section: MenuSection
  label: string
  items: ResolvedMenuItem[]
}

/** The principal sees every page view-only (Omar, 31 Oct 2026). The server refuses his writes
 *  (403); this only drives the banner, the disabled controls and the client-side stop. */
export function isReadOnlyRole(role: Role | undefined | null): boolean {
  return role === 'PRINCIPAL'
}

/** The tabs of a hub this user may open. */
export function hubTabs(hub: HubKey, u: MenuUser): HubTab[] {
  return HUBS[hub].filter((t) => (!t.showIf || t.showIf(u)) && routeAllows(t.route, u.role))
}

function routeAllows(route: string, role: Role): boolean {
  const roles = ROUTE_ROLES[route] ?? ['ADMIN', 'SCHOOL_ADMIN', 'PRINCIPAL', 'TEACHER']
  return roles.includes(role)
}

/** The hub whose tab owns this pathname (longest matching tab route), if any. */
export function hubFor(pathname: string, u: MenuUser): { hub: HubKey; tabs: HubTab[]; active: string } | null {
  let best: { hub: HubKey; route: string } | null = null
  for (const hub of Object.keys(HUBS) as HubKey[]) {
    for (const t of HUBS[hub]) {
      if ((pathname === t.route || pathname.startsWith(t.route + '/')) && (!best || t.route.length > best.route.length)) {
        best = { hub, route: t.route }
      }
    }
  }
  if (!best) return null
  const tabs = hubTabs(best.hub, u)
  if (tabs.length < 2 || !tabs.some((t) => t.route === best!.route)) return null
  return { hub: best.hub, tabs, active: best.route }
}

/** Resolve the visible sidebar for a user (drops conditional items that fail showIf). */
export function buildMenu(u: MenuUser): ResolvedMenuSection[] {
  const spec = MENU_BY_ROLE[u.role as StaffRole] ?? []
  return spec
    .map((s) => ({
      section: s.section,
      label: SECTION_LABEL[s.section],
      items: s.entries
        .filter((e) => !e.showIf || e.showIf(u))
        .map((e): ResolvedMenuItem => {
          const it: MenuItem = ITEMS[e.item]
          if (!it.hub) return { ...it, routes: [it.route] }
          const tabs = hubTabs(it.hub, u)
          // A hub entry opens its first tab this user may open.
          return { ...it, route: tabs[0]?.route ?? it.route, routes: tabs.map((t) => t.route) }
        }),
    }))
    .filter((s) => s.items.length > 0)
}

/**
 * Hard route guard (RequireRole), keyed to the backend authorization tier — the menu controls
 * discoverability, this controls access. ADMIN retains access to curriculum routes even though
 * they're hidden from the admin sidebar (matches Flutter; backend remains the final authority: 403).
 */
// The school's two roles: Omar, 31 Oct 2026, "two roles, one menu, the principal is read-only".
// Every page the school admin opens, the principal opens too (view-only); pages that only CREATE
// something (new video, new live session, new exam) stay closed to him.
const SCHOOL: Role[] = ['ADMIN', 'SCHOOL_ADMIN', 'PRINCIPAL']
const SCHOOL_WRITE: Role[] = ['ADMIN', 'SCHOOL_ADMIN']
const ALL_STAFF: Role[] = ['ADMIN', 'SCHOOL_ADMIN', 'PRINCIPAL', 'TEACHER']

export const ROUTE_ROLES: Record<string, Role[]> = {
  '/home': ALL_STAFF,
  '/schools': ['ADMIN'],
  '/schools/:schoolId': ['ADMIN'],
  '/users': SCHOOL,
  '/users/:userId/activity': SCHOOL,
  '/users/:userId/subscription': SCHOOL,
  '/subscription-problems': SCHOOL,
  // Platform admin ONLY. Omar, 2026-09-24: "Approving stays at platform
  // admin. Do not widen it." The API enforces the same thing; this keeps
  // the screen out of a school admin's menu so it is not even offered.
  '/receipts': ['ADMIN'],
  // Telegram notifications, 29 Sep 2026: platform admin only (the API agrees).
  '/telegram': ['ADMIN'],
  // Phone codes and updates, 18 Oct 2026: platform admin only (the API agrees).
  '/phone-codes': ['ADMIN'],
  // App Versions (31 Oct 2026): platform admin only (the API agrees).
  '/app-versions': ['ADMIN'],
  // google-apple-1021 (21 Oct 2026): platform admin only (the API agrees).
  '/sign-in-methods': ['ADMIN'],
  // --- Novi Wardrobe (21 Oct 2026, wardrobe-items-1022): platform admin only (the API agrees) ---
  '/mascot-wardrobe': ['ADMIN'],
  // the free plan and the streak, 30 Oct 2026: platform admin only (the API agrees).
  '/free-plan': ['ADMIN'],
  // every child's streak, rebuilt (4 Nov 2026): platform admin only (the API agrees).
  '/streaks': ['ADMIN'],
  '/support/guests': ['ADMIN'],
  // Free or paid per country, 21 Oct 2026: platform admin only (the API agrees).
  '/feature-access': ['ADMIN'],
  // Rollout switches (app 1.0.126, 21 Oct 2026): platform admin only (the API agrees and audits).
  '/rollout': ['ADMIN'],
  // Activity (9 Oct 2026), "Engagement" in the menu since 31 Oct: the school's two roles and the
  // platform admin (with cost). Teachers never had it.
  '/activity': SCHOOL,
  // The two old addresses only redirect to /activity (saved links keep working).
  '/usage': SCHOOL,
  '/student-activity': SCHOOL,
  // Registrations (1 Nov 2026): platform admin (every school) and the school's two roles (own school; the API agrees).
  '/registrations': SCHOOL,
  // Class view (9 Oct 2026) = the "Grade table" tab of Students since 31 Oct. A teacher sees his
  // grades only, and in them only his subjects (the API scopes it, app/core/teacher_scope.py).
  '/class-view': ALL_STAFF,
  '/class-view/students/:studentId': ALL_STAFF,
  '/grades': SCHOOL,
  '/subjects': SCHOOL,
  // Grades only, no classes (Omar, 31 Oct 2026): the address now redirects to /grades.
  '/classes': SCHOOL,
  '/books': SCHOOL,
  // Reviewing a newly uploaded book is the default school's admin's job, not
  // only the platform admin's. Backend remains the authority (403).
  '/books/:bookId/review': SCHOOL,
  '/dashboard': ALL_STAFF,
  '/students': ALL_STAFF,
  '/students/:studentId': ALL_STAFF,
  // Omar, 15 Oct 2026: the school admin reads the exam list (no create, edit or on/off).
  '/tests': ALL_STAFF,
  '/tests/create': ['TEACHER', 'ADMIN'],
  // Curriculum coverage (2 Nov 2026): platform admin (any school), school admin and principal (own school). The API agrees.
  '/coverage': SCHOOL,
  '/tests/:testId/edit': ['TEACHER', 'ADMIN'],
  '/question-bank': ALL_STAFF,
  '/teacher-questions': ALL_STAFF,
  '/teacher-questions/:questionId': ALL_STAFF,
  '/videos': SCHOOL,
  '/videos/upload': SCHOOL_WRITE,
  '/live-sessions': SCHOOL,
  '/live-sessions/new': SCHOOL_WRITE,
  '/live-sessions/:sessionId/edit': SCHOOL,
  '/live-sessions/:sessionId/follow-up': SCHOOL,  // paid live sessions (21 Oct 2026)
  '/announcements': SCHOOL,
  '/timetable': ALL_STAFF,
  // The school admin got the leaderboard on 31 Oct 2026 (the principal had it, he did not).
  '/leaderboard': ALL_STAFF,
  '/challenge-activity': ALL_STAFF,
  // Challenge settings, 12 Oct 2026: the same people as the first-place announcement. The API agrees.
  '/challenge-settings': SCHOOL,
  '/monitoring': ['ADMIN'],
  '/cache': ['ADMIN'],
  '/monitoring/cost-explorer': ['ADMIN'],
  // Running vs one-time from the real bill (1 Nov 2026): platform admin only (the API agrees).
  '/monitoring/running': ['ADMIN'],
  '/support': SCHOOL,
  '/support/:conversationId': SCHOOL,
  '/support/knowledge': SCHOOL,
  '/test-prompts': ['ADMIN'],
  '/profile': ALL_STAFF,
  // Automatic notifications, 31 Oct 2026: a school admin (and the read-only principal) sees their own school only.
  '/auto-pushes': SCHOOL,
}
